import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
  DirectionsCar as CarIcon,
  Star as StarIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { carsAPI } from '../services/apiService';
import { carTypesAPI } from '../services/apiService';

const CarManager = ({ driverId, driverName, onClose, dialogMode = false }) => {
  const [cars, setCars] = useState([]);
  const [carTypes, setCarTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [editForm, setEditForm] = useState({
    make: '', model: '', year: '', color: '', licensePlate: '', seats: '', type: '',
  });

  // Disable dialog state
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disablingCar, setDisablingCar] = useState(null);
  const [disableReason, setDisableReason] = useState('');

  useEffect(() => {
    loadCars();
    carTypesAPI.getAll().then(data => {
      setCarTypes((data || []).sort((a, b) => a.sortOrder - b.sortOrder));
    }).catch(() => { });
  }, [driverId]);

  const loadCars = async () => {
    try {
      setLoading(true);
      const data = await carsAPI.getByDriver(driverId);
      setCars(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load cars:', error);
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────────────
  const openEditDialog = (car) => {
    setEditingCar(car);
    setEditForm({
      make: car.make || '',
      model: car.model || '',
      year: car.year ? String(car.year) : '',
      color: car.color || '',
      licensePlate: car.licensePlate || '',
      seats: car.seats ? String(car.seats) : '',
      type: car.type ?? '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.make.trim() || !editForm.model.trim() || !editForm.year.trim() || !editForm.licensePlate.trim()) {
      return;
    }
    setSaving(true);
    try {
      await carsAPI.dispatcherUpdate({
        carId: editingCar.carId,
        driverId: editingCar.driverId,
        make: editForm.make.trim(),
        model: editForm.model.trim(),
        year: parseInt(editForm.year),
        color: editForm.color.trim(),
        licensePlate: editForm.licensePlate.trim().toUpperCase(),
        seats: parseInt(editForm.seats) || editingCar.seats,
        type: editForm.type,
        isPrimary: editingCar.isPrimary,
      });
      setEditDialogOpen(false);
      await loadCars();
    } catch (error) {
      console.error('Failed to update car:', error);
    } finally {
      setSaving(false);
    }
  };

  // ── Disable ─────────────────────────────────────────────────────────────────
  const openDisableDialog = (car) => {
    setDisablingCar(car);
    setDisableReason('');
    setDisableDialogOpen(true);
  };

  const handleDisable = async () => {
    if (!disableReason.trim()) return;
    setSaving(true);
    try {
      await carsAPI.disable(disablingCar.carId, disableReason.trim());
      setDisableDialogOpen(false);
      await loadCars();
    } catch (error) {
      console.error('Failed to disable car:', error);
    } finally {
      setSaving(false);
    }
  };

  // ── Enable ──────────────────────────────────────────────────────────────────
  const handleEnable = async (car) => {
    setSaving(true);
    try {
      await carsAPI.enable(car.carId);
      await loadCars();
    } catch (error) {
      console.error('Failed to enable car:', error);
    } finally {
      setSaving(false);
    }
  };

  const getCarTypeName = (typeId) => {
    const found = carTypes.find(t => t.id === typeId);
    return found ? found.name : `Type ${typeId}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!dialogMode && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={onClose} variant="outlined">
              Back to Drivers
            </Button>
            <Typography variant="h5" component="h2">
              Vehicles for {driverName}
            </Typography>
          </Box>
        </Paper>
      )}

      {!Array.isArray(cars) || cars.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <CarIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No vehicles found for this driver.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {cars.map(car => (
            <Grid item xs={12} sm={6} lg={4} key={car.carId}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                opacity: car.isActive === false ? 0.65 : 1,
                border: car.isPrimary
                  ? '2px solid #1976d2'
                  : car.isActive === false
                    ? '2px solid #d32f2f'
                    : '1px solid #e0e0e0',
                position: 'relative',
              }}>
                {/* Status badges */}
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 1 }}>
                  {car.isPrimary && (
                    <Chip icon={<StarIcon />} label="Primary" color="primary" size="small" />
                  )}
                  {car.isActive === false && (
                    <Chip icon={<BlockIcon />} label="Disabled" color="error" size="small" />
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1, pt: car.isPrimary || car.isActive === false ? 5 : 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {car.year} {car.make} {car.model}
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">License Plate</Typography>
                      <Typography variant="body1">{car.licensePlate || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Color</Typography>
                      <Typography variant="body1">{car.color || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Type</Typography>
                      <Typography variant="body1">{getCarTypeName(car.type)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Seats</Typography>
                      <Typography variant="body1">{car.seats ?? 'N/A'}</Typography>
                    </Grid>
                  </Grid>

                  {car.isActive === false && car.disabledReason && (
                    <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
                      <Typography variant="caption"><strong>Reason:</strong> {car.disabledReason}</Typography>
                    </Alert>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', gap: 1, px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    variant="outlined"
                    onClick={() => openEditDialog(car)}
                    disabled={saving}
                  >
                    Edit
                  </Button>
                  {car.isActive === false ? (
                    <Button
                      size="small"
                      startIcon={<EnableIcon />}
                      variant="contained"
                      color="success"
                      onClick={() => handleEnable(car)}
                      disabled={saving}
                    >
                      Enable
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<BlockIcon />}
                      variant="contained"
                      color="error"
                      onClick={() => openDisableDialog(car)}
                      disabled={saving}
                    >
                      Disable
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Edit Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Vehicle
          <IconButton onClick={() => setEditDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Make *"
                fullWidth
                value={editForm.make}
                onChange={e => setEditForm({ ...editForm, make: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Model *"
                fullWidth
                value={editForm.model}
                onChange={e => setEditForm({ ...editForm, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Year *"
                fullWidth
                type="number"
                value={editForm.year}
                onChange={e => setEditForm({ ...editForm, year: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Color"
                fullWidth
                value={editForm.color}
                onChange={e => setEditForm({ ...editForm, color: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="License Plate *"
                fullWidth
                value={editForm.licensePlate}
                onChange={e => setEditForm({ ...editForm, licensePlate: e.target.value })}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Seats"
                fullWidth
                type="number"
                value={editForm.seats}
                onChange={e => setEditForm({ ...editForm, seats: e.target.value })}
              />
            </Grid>
            {carTypes.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Car Type</InputLabel>
                  <Select
                    value={editForm.type}
                    label="Car Type"
                    onChange={e => setEditForm({ ...editForm, type: e.target.value })}
                  >
                    {carTypes.map(ct => (
                      <MenuItem key={ct.id} value={ct.id}>{ct.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving || !editForm.make.trim() || !editForm.model.trim() || !editForm.year.trim() || !editForm.licensePlate.trim()}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Disable Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Disable Vehicle
          <IconButton onClick={() => setDisableDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {disablingCar && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Disabling: <strong>{disablingCar.year} {disablingCar.make} {disablingCar.model}</strong>
              {disablingCar.isPrimary && (
                <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                  ⚠️ This is the driver's primary vehicle. Another vehicle will be auto-promoted if available.
                </Typography>
              )}
            </Typography>
          )}
          <TextField
            label="Reason *"
            fullWidth
            multiline
            rows={3}
            value={disableReason}
            onChange={e => setDisableReason(e.target.value)}
            placeholder="e.g. Vehicle is too old (pre-2016)"
            autoFocus
            inputProps={{ maxLength: 200 }}
            helperText={`${disableReason.length}/200 — The driver will see this reason in their push notification.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisable}
            disabled={saving || !disableReason.trim()}
          >
            {saving ? <CircularProgress size={20} /> : 'Disable Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarManager;